import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarService } from '../services/calendar.service';
import { useToast } from '@/hooks/use-toast';

export function useCalendar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const calendarsQuery = useQuery({
    queryKey: ['calendars'],
    queryFn: () => calendarService.getCalendars(),
  });

  const eventsQuery = useQuery({
    queryKey: ['calendar-events'],
    queryFn: () => calendarService.getEvents(),
  });

  const syncMutation = useMutation({
    mutationFn: () => calendarService.triggerSync(),
    onSuccess: () => {
      toast({
        title: 'Sincronização iniciada',
        description: 'Os eventos serão atualizados em breve.',
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['calendars'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao sincronizar',
        description: error.message || 'Não foi possível iniciar a sincronização.',
        variant: 'destructive',
      });
    },
  });

  return {
    calendars: calendarsQuery.data || [],
    events: eventsQuery.data || [],
    isLoading: calendarsQuery.isLoading || eventsQuery.isLoading,
    refetch: () => {
      calendarsQuery.refetch();
      eventsQuery.refetch();
    },
    sync: syncMutation.mutate,
    isSyncing: syncMutation.isPending,
  };
}

export function useCalendarEvents(startDate?: Date, endDate?: Date) {
  return useQuery({
    queryKey: ['calendar-events', startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => {
      const start = startDate?.toISOString();
      const end = endDate?.toISOString();
      return calendarService.getEvents(start, end);
    },
    enabled: true,
  });
}

export function useCalendarEvent(eventId: string | null) {
  return useQuery({
    queryKey: ['calendar-event', eventId],
    queryFn: () => {
      if (!eventId) throw new Error('Event ID is required');
      return calendarService.getEventById(eventId);
    },
    enabled: !!eventId,
  });
}

